import { useState, useMemo, useCallback } from 'preact/hooks';
import { css } from '@emotion/react';
import { defaultCoinUrl } from '@/assets/coin';
import { IPaymentChoice } from '@/index';
import { CoinInfoDisplay } from './coin-status';
import { ChargeGrid } from './charge-grid';
import { useOrientation } from '@/hooks/useOrientation';
import { useTranslation } from '@/hooks/useTransition';
import { Toggle } from './toggle';
import { Close } from '../../../assets/close';
import { defaultGemImage } from '@/assets/coin';
interface CoinConfirmProps {
  currency: 'JOLI_COIN' | 'JOLI_GEM';
  unlockNeedsAmount: number;
  balanceAmount: number;
  defaultEnableAutoDeduct: boolean;
  confirmButtonKey: string;
  cancelButtonKey: string;
  onConfirm: (enableAutoDeduct: boolean) => void;
  onClose: (params: { type?: 'CANCEL' | 'FAILED' | 'CONFIRM' }) => void;
  onEnableDeductChanged: (enableAutoDeduct: boolean) => void;
}

export const styles = {
  modal: css`
    font-family: PingFangSC-Regular;
    background-color: #1a1a1a;
    color: white;
    border-radius: 16px 16px 0 0;
    padding: 16px;
    padding-right: 10px;
    width: calc(100% - 32px);
    max-width: 720px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  closeButton: css`
    background: none;
    border: none;
    cursor: pointer;
    color: white;
    padding: 0px;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-right: 8px;
  `,
  title: css`
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
    color: #fff;
    padding: 8px 0;
    text-align: center;
  `,
  autoDeductOption: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    -webkit-tap-highlight-color: transparent;
  `,
  buttonContainer: css`
    display: flex;
    width: 100%;
    gap: 16px;
    padding-top: 0px;
    padding-bottom: max(var(--navigation-bar-height), 12px);

    @media (orientation: landscape) {
      flex-direction: row;
      gap: 16px;
    }
  `,
  buttonFlex: css`
    width: 100%;
    flex: 1 1 100%;

    @media (orientation: landscape) {
      width: 50%;
      flex: 1 1 50%;
    }
  `,
  confirmButton: css`
    background-color: #6a3de8;
    color: white;
    border: none;
    border-radius: 40px;
    padding: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s;
    -webkit-tap-highlight-color: transparent;
  `,
  cancelButton: css`
    background-color: transparent;
    color: #d9d9d9;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 40px;
    padding: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s;
    -webkit-tap-highlight-color: transparent;
  `,
  bottomIndicator: css`
    width: 40px;
    height: 4px;
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    margin: 0 auto;
    -webkit-tap-highlight-color: transparent;
  `
};

export const CoinConfirmContent: React.FC<CoinConfirmProps> = ({
  unlockNeedsAmount = 10,
  balanceAmount = 999,
  defaultEnableAutoDeduct = false,
  currency = 'JOLI_COIN',
  onConfirm,
  onClose,
  onEnableDeductChanged,
  confirmButtonKey,
  cancelButtonKey
}) => {
  const [enableAutoDeduct, setEnableAutoDeduct] = useState(defaultEnableAutoDeduct);
  const orientation = useOrientation();
  const { t } = useTranslation();
  const handleConfirm = () => {
    onConfirm(enableAutoDeduct);
  };

  const coinUrl = useMemo(() => {
    if (currency === 'JOLI_GEM') {
      return defaultGemImage;
    }
    return defaultCoinUrl;
  }, [currency]);

  const unlockWithJolicoinKey = useMemo(() => {
    if (currency === 'JOLI_GEM') {
      return 'joligem.unlockWithJolicoin';
    }
    return 'jolicoin.unlockWithJolicoin';
  }, [currency]);

  const unlockWithJolicoinDescriptionKey = useMemo(() => {
    if (currency === 'JOLI_GEM') {
      return 'joligem.unlockWithJolicoinDescription';
    }
    return 'jolicoin.unlockWithJolicoinDescription';
  }, [currency]);

  const toggleEnableAutoDeduct = useCallback(() => {
    setEnableAutoDeduct(!enableAutoDeduct);
    onEnableDeductChanged(!enableAutoDeduct);
  }, [enableAutoDeduct]);

  return (
    <div
      style={`${styles.modal.styles} ${orientation === 'portrait' ? 'max-width: 440px' : 'max-width: 75%'}`}
    >
      <div style={styles.header.styles}>
        <CoinInfoDisplay
          unlockNeedsAmount={unlockNeedsAmount}
          balanceAmount={balanceAmount}
          coinImage={coinUrl}
          currency={currency}
        />

        <button style={styles.closeButton.styles} onClick={onClose.bind(null, { type: 'CANCEL' })}>
          <Close />
        </button>
      </div>

      <div style={styles.content.styles}>
        <h2 style={styles.title.styles}>{t(unlockWithJolicoinKey)}</h2>

        {!defaultEnableAutoDeduct && (
          <div style={styles.autoDeductOption.styles}>
            <span
              style={
                css`
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 1.2;
                  color: #979797;
                `.styles
              }
            >
              {t(unlockWithJolicoinDescriptionKey)}
            </span>
            <Toggle enableAutoDeduct={enableAutoDeduct} toggleEnableAutoDeduct={toggleEnableAutoDeduct} />
          </div>
        )}

        <section
          style={`${styles.buttonContainer.styles} ${
            orientation === 'landscape' ? 'flex-direction: row' : 'flex-direction: column'
          }`}
        >
          <button
            style={`${styles.confirmButton.styles} ${styles.buttonFlex.styles}`}
            onClick={handleConfirm}
          >
            {t(confirmButtonKey)}
          </button>

          <button
            style={`${styles.cancelButton.styles} ${styles.buttonFlex.styles}`}
            onClick={onClose.bind(null, { type: 'FAILED' })}
          >
            {t(cancelButtonKey)}
          </button>
        </section>
      </div>
    </div>
  );
};

interface CoinPaymentProps {
  currency: 'JOLI_COIN' | 'JOLI_GEM';
  balanceAmount: number;
  unlockNeedsAmount: number;
  defaultEnableAutoDeduct: boolean;
  paymentChoices: IPaymentChoice[];
  onConfirm: (productId: string) => void;
  onClose: (params: { type?: 'CANCEL' | 'FAILED' | 'CONFIRM' }) => void;
  onEnableDeductChanged: (enalbed: boolean) => void;
  confirmButtonKey: string;
  cancelButtonKey: string;
  onSelect: (productId: string) => void;
}

export const CoinPaymentContent: React.FC<CoinPaymentProps> = ({
  unlockNeedsAmount = 1,
  balanceAmount = 0,
  defaultEnableAutoDeduct = false,
  paymentChoices = [],
  currency = 'JOLI_COIN',
  onConfirm,
  onClose,
  onEnableDeductChanged,
  onSelect,
  confirmButtonKey,
  cancelButtonKey
}) => {
  const [enableAutoDeduct, setEnableAutoDeduct] = useState(defaultEnableAutoDeduct);
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(() => {
    const defaultSelected = paymentChoices.find((choice) => choice.defaultSelected);
    return defaultSelected?.productId || paymentChoices?.[0]?.productId;
  });

  const orientation = useOrientation();
  const handleConfirm = () => {
    onConfirm(selectedId);
  };

  const handleSelect = useCallback((id: string) => {
    console.log(id);
    setSelectedId(id);
    onSelect(id);
  }, []);

  const toggleEnableAutoDeduct = useCallback(() => {
    setEnableAutoDeduct(!enableAutoDeduct);
    onEnableDeductChanged(!enableAutoDeduct);
  }, [enableAutoDeduct]);

  const coinUrl = useMemo(() => {
    if (currency === 'JOLI_GEM') {
      return defaultGemImage;
    }
    return defaultCoinUrl;
  }, [currency]);

  const unlockWithJolicoinDescriptionKey = useMemo(() => {
    if (currency === 'JOLI_GEM') {
      return 'joligem.unlockWithJolicoinDescription';
    }
    return 'jolicoin.unlockWithJolicoinDescription';
  }, [currency]);

  console.log('defaultEnableAutoDeduct', defaultEnableAutoDeduct);

  return (
    <div style={styles.modal.styles}>
      <div style={styles.header.styles}>
        <CoinInfoDisplay
          unlockNeedsAmount={unlockNeedsAmount}
          balanceAmount={balanceAmount}
          coinImage={coinUrl}
          currency={currency}
        />

        <button style={styles.closeButton.styles} onClick={onClose.bind(null, { type: 'CANCEL' })}>
          <Close />
        </button>
      </div>

      <div style={styles.content.styles}>
        <div
          style={
            css`
              padding: 16px 0px;
            `.styles
          }
        >
          <ChargeGrid
            options={paymentChoices.map((choice) => ({
              id: choice.productId,
              coins: choice.quantity,
              price: choice.totalAmountStr
            }))}
            coinImageSrc={coinUrl}
            selectedId={selectedId}
            currency={currency}
            onSelect={(option) => handleSelect(option.id)}
          />
        </div>

        {!defaultEnableAutoDeduct && (
          <div style={styles.autoDeductOption.styles}>
            <span
              style={
                css`
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 1.2;
                  color: #979797;
                `.styles
              }
            >
              {t(unlockWithJolicoinDescriptionKey)}
            </span>
            <Toggle enableAutoDeduct={enableAutoDeduct} toggleEnableAutoDeduct={toggleEnableAutoDeduct} />
          </div>
        )}

        <section
          style={`${styles.buttonContainer.styles} ${
            orientation === 'landscape' ? 'flex-direction: row' : 'flex-direction: column'
          }`}
        >
          <button
            style={`${styles.confirmButton.styles} ${styles.buttonFlex.styles}`}
            onClick={handleConfirm}
          >
            {t(confirmButtonKey)}
          </button>

          <button
            style={`${styles.cancelButton.styles} ${styles.buttonFlex.styles}`}
            onClick={onClose.bind(null, { type: 'FAILED' })}
          >
            {t(cancelButtonKey)}
          </button>
        </section>
      </div>
    </div>
  );
};
